import React, { useContext } from "react";
import { VariantContextData } from "../variants/VariantBuilderContext";
import useConditions from "../conditions/useConditions";
import { isProductHeroBuiltinContentVisible } from "../../utils/builderUtils";

const useContentToggle = (Sections) => {
  const [contextData, builder, setContextData] = useContext(VariantContextData);
  let variant = contextData.objects.variant;

  const { Conditions } = useConditions();

  const ContentToggleMapping = (elementType, elementId) => {
    const mapping = {
      collapse: {
        title: "Collapse Title",
      },
      tabs: {
        title: "Tab Title",
      },
      carousel: {
        title: "Carousel Title",
      },
      conditional_row: {
        title: "Conditional Row",
      },
      product_hero: {
        title: "Custom Element",
      }
    };

    return mapping[elementType];
  };

  const ContentToggle = {
    create: async function (element, options) {
      const elementType = element.element_type;
      const elementTypeMapping = ContentToggleMapping(elementType, element.id);

      let position =
        Object.values(variant.content_toggle_items).filter(
          (list_item) =>
            list_item.element_id == element.id && list_item.toBeDeleted !== true
        )?.sort((a, b) => b.position - a.position)?.[0]?.position + 1 || 1;

      builder.create(
        "content_toggle_items",
        {
          position: position,
          element_id: element.id,
          content: `${elementTypeMapping["title"]} ${position}`,
        },
        {
          callback: (content_toggle_item) => {
            let newContextData = { ...contextData };
            newContextData.objects.variant.sections[content_toggle_item.section.id] =
            content_toggle_item.section;
            setContextData(newContextData);

            if (options && options.callback) {
              options.callback(content_toggle_item);
            }
          },
        }
      );
    },

    duplicate: async function (og_toggle_item, payload, skip_history) {
      let attributes = {
        position: og_toggle_item.position,
        content: og_toggle_item.content,
        options: og_toggle_item.options,
        element_id: og_toggle_item.element_id,
        create_initial_section: false,
      };

      if (payload) {
        attributes = { ...attributes, ...payload };
      }

      // If the field is being duplicated within the same element, we need to increment the position
      if (skip_history !== true) {
        let position =
          Object.values(variant.content_toggle_items).filter(
            (list_item) =>
              list_item.element_id == og_toggle_item.element_id && og_toggle_item.toBeDeleted !== true
          )?.sort((a, b) => b.position - a.position)?.[0]?.position + 1 || 1;

        attributes.position = position;
      }

      return new Promise((resolve, reject) => {
        builder.create("content_toggle_items", attributes, {
          callback: async function (toggle_item) {
            try {
              let og_section =
                Object.values(variant.sections).filter(
                  (section) =>
                    section.content_toggle_item_id == og_toggle_item.id &&
                    section.toBeDeleted !== true
                )[0];

              const element = variant.elements[toggle_item.element_id];
              const section = variant.sections[element.section_id];

              let og_conditions = Object.values(variant.conditions).filter(
                (condition) => condition.contactable_id == og_toggle_item.id &&
                  condition.contactable_type == "ContentToggleItem" &&
                  condition.toBeDeleted !== true
              );

              await Promise.all([
                ...(og_section
                  ? [
                      Sections.duplicate(
                        og_section,
                        {
                          element_id: toggle_item.element_id,
                          step_id: section.step_id,
                          content_toggle_item_id: toggle_item.id,
                        },
                        true
                      ),
                    ]
                  : []),
                ...og_conditions.map((condition) => {
                  return Conditions.duplicate(
                    condition,
                    {
                      contactable_id: toggle_item.id,
                    },
                    true
                  );
                })
              ]);

              resolve(toggle_item);
            } catch (error) {
              reject(error);
            }
          },
          skip_history: skip_history,
          skip_select: true,
        });
      });
    },

    remove: (toggle_item) => {
      let message =
        "Are you sure you want to remove this item? Once your changes are saved, it will be irreversibly deleted.";

      builder.remove("content_toggle_items", toggle_item.id, message);
    },

    productHeroContentBlocks: (element) => {
      const product_hero_builtin_content_keys = [
        "offer-badge",
        "reviews",
        "title",
        "price",
        "description",
        "quantity-picker",
        "variant-picker",
        "subscription",
        "button",
      ];


      const builtinBlocks = product_hero_builtin_content_keys
        .filter((key) => isProductHeroBuiltinContentVisible(element, key))
        .map((key) => ({
          object_type: "elements",
          object_id: element.id,
          setting_name: `[options][${key}-position]`,
          position: parseInt(element.options[`${key}-position`]),
        }))
        .filter((block) => !Number.isNaN(block.position));

      const customBlocks = Object.values(variant.content_toggle_items)
        .filter(
          (item) =>
            item.element_id == element.id &&
            item.toBeDeleted !== true &&
            (item.options && item.options["show"]) !== "false"
        )
        .map((item) => ({
          object_type: "content_toggle_items",
          object_id: item.id,
          setting_name: "[position]",
          position: item.position,
        }));

      return [...builtinBlocks, ...customBlocks].sort(
        (a, b) => a.position - b.position
      );
    },

    swapContentBlock: (toggle_item, direction) => {
      const element = variant.elements[toggle_item.element_id];
      if (!element) {
        return;
      }

      const blocks = ContentToggle.productHeroContentBlocks(element);
      const index = blocks.findIndex(
        (block) =>
          block.object_type == "content_toggle_items" &&
          block.object_id == toggle_item.id
      );
      const swapIndex = direction == "up" ? index - 1 : index + 1;

      if (index == -1 || swapIndex < 0 || swapIndex >= blocks.length) {
        return;
      }

      const current = blocks[index];
      const neighbor = blocks[swapIndex];
      const positionUpdate = (block, value) => ({
        object_type: block.object_type,
        object_id: block.object_id,
        setting_name: block.setting_name,
        value: value,
        old_value: block.position,
      });

      builder.update([
        positionUpdate(current, neighbor.position),
        positionUpdate(neighbor, current.position),
      ]);
    },

    moveUp: (toggle_item) => {
      ContentToggle.swapContentBlock(toggle_item, "up");
    },

    moveDown: (toggle_item) => {
      ContentToggle.swapContentBlock(toggle_item, "down");
    },

    sortableStart: (list_item) => {
      let containerSelector =
        '.sortable-container[data-sortable-name="content-toggle-list-items"][data-object_type="content_toggle_items"]';
      let container = document.querySelector(containerSelector);

      let item_selector =
        containerSelector +
        " .setting[data-setting_type='item_v2'][data-object_type='content_toggle_items']";

      let sortable_options = {
        sort: true,
        group: { name: "content-list-item-position" },
        animation: 150,
        draggable: item_selector,
        ghostClass: "drop-zone",
        handle: ".item-trigger[data-object_type='content_toggle_items'], .cf-item-v2",
        forceFallback: true,
        fallbackOnBody: true,
        fallbackClass: "builder-sortable-fallback",
        fallbackTolerance: 5,
        delay: 10,
        scrollSensitivity: 100,
        scrollSpeed: 10,
        swapThreshold: 0.8,
        onUpdate: function (event) {
          ContentToggle.sortableUpdate(event, list_item);
        },
      };

      if (container) {
        new Sortable(container, sortable_options);
      }
    },

    sortableUpdate: (event) => {
      let updates = [];

      let counter = 0;
      Array.from(event.to.children).forEach((list_item_div) => {
        counter++;

        let object_id = list_item_div.getAttribute("data-object_id");
        if (object_id) {
          let list_item_id = parseInt(object_id);

          updates.push({
            object_type: "content_toggle_items",
            object_id: list_item_id,
            setting_name: "[position]",
            value: counter,
          });
        }
      });

      builder.update(updates);
    },

    layersSortableStart: (element_id) => {
      let container = document.querySelector(`#layers .layers-container[data-object-type='elements'][data-object-id="${element_id}"]`);

      let sortable_options = {
        sort: true,
        group: { name: "layers-content-toggle-items" },
        animation: 150,
        draggable: `.layers-item[data-object-type='content_toggle_items'][data-field-is-child='false']`,
        ghostClass: "drop-zone",
        handle: `.layers-item-icon[data-object-type='content_toggle_items'][data-field-is-child='false'], .layers-item-label[data-object-type='content_toggle_items'][data-field-is-child='false']`,
        scroll: true,
        scrollSensitivity: 100,
        forceFallback: true,
        fallbackOnBody: true,
        fallbackTolerance: 5,
        fallbackClass: 'hide-during-drag',
        scrollSpeed: 10,
        onUpdate: function (event) {
          ContentToggle.layersSortableUpdate(event);
        },
        onAdd: function (event) {
          const element = contextData?.objects?.variant?.elements?.[element_id];

          if (element && ['carousel', 'conditional_row', 'collapse', 'tabs'].includes(element.element_type)) {
            var itemEl = event.item;
            let origParent = event.from;
            origParent.appendChild(itemEl);

            ContentToggle.layersSortableAdd(event);
          } else {
            var itemEl = event.item;
            let origParent = event.from;
            origParent.appendChild(itemEl);
          }
        },
      };

      if (container) {
        new Sortable(container, sortable_options);
      }
    },

    layersSortableUpdate: (event) => {
      let updates = [];

      Array.from(event.to.children).forEach((list_item_div, i) => {
        let new_position = i + 1;
        let object_id = list_item_div.getAttribute("data-object-id");
        if (object_id) {
          let list_item_id = parseInt(object_id);

          updates.push({
            object_type: "content_toggle_items",
            object_id: list_item_id,
            setting_name: "[position]",
            value: new_position,
          });
        }
      });

      builder.update(updates);
    },

    layersSortableAdd: (event) => {
      let updates = [];

      let target_position = event.newIndex + 1;
      let new_position = 1;

      updates.push({
        object_type: "content_toggle_items",
        object_id: event.item.getAttribute("data-object-id"),
        setting_name: "[position]",
        value: target_position,
      });

      Array.from(event.to.children).forEach((list_item_div, i) => {
        let object_id = list_item_div.getAttribute("data-object-id");

        if (new_position == target_position) {
          new_position += 1;
        }

        if (object_id) {
          let list_item_id = parseInt(object_id);

          updates.push({
            object_type: "content_toggle_items",
            object_id: list_item_id,
            setting_name: "[position]",
            value: new_position,
          });
        }

        new_position += 1;
      });

      if (event.to !== event.from) {
        const elementFrom = event.from.getAttribute("data-object-id");
        const elementTo = event.to.getAttribute("data-object-id");
        const list_item_id = parseInt(event.item.getAttribute("data-object-id"));
        const section = Object.values(contextData?.objects?.variant?.sections).filter(
          (section) => section.content_toggle_item_id == list_item_id && section.element_id == elementFrom
        )[0];

        const findParentStep = (object) => {
          if (!object) {
            return null;
          }
          if (object.getAttribute("data-object-type") == "steps") {
            return object;
          }
          return findParentStep(object.parentElement);
        };

        const parentStepFrom = findParentStep(event.from);
        const parentStepTo = findParentStep(event.to);
        let stepFrom = parentStepFrom ? parentStepFrom.getAttribute("data-object-id") : null;
        let stepTo = parentStepTo ? parentStepTo.getAttribute("data-object-id") : null;

        if (stepFrom !== stepTo) {
          updates.push({
            object_type: "sections",
            object_id: section.id,
            setting_name: "[step_id]",
            value: stepTo,
            old_value: stepFrom,
          });
        }

        updates.push({
          object_type: "sections",
          object_id: section.id,
          setting_name: "[element_id]",
          value: elementTo,
          old_value: elementFrom,
        });

        updates.push({
          object_type: "content_toggle_items",
          object_id: list_item_id,
          setting_name: "[element_id]",
          value: elementTo,
          old_value: elementFrom,
        });
      }

      builder.update(updates);
    },
  };

  let currentHook = ContentToggle;
  return { ContentToggle, currentHook };
};

export default useContentToggle;
